import React, { useRef } from 'react';
import { Box, Divider, TextField, Typography, InputAdornment } from '@mui/material';
import {
  FormProps,
  FormHeader,
  ClientNameField,
  DocumentUploadArea,
  AudioRecordingSection,
  WritingStyleSelector,
  InfoTip,
} from './shared';

const TutelaCautelarForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedStyle, setSelectedStyle] = React.useState(values.estilo_escrita || 'padrao');

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  const handleRecordingComplete = (audioBlob: Blob) => {
    const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, {
      type: 'audio/webm',
    });
    onFileUpload([audioFile]);
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Tutela Provisória Cautelar Antecedente"
        customDescription="Medida de urgência para assegurar o resultado útil do processo"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* 1. Nome do Cliente */}
        <ClientNameField
          value={values.nome_cliente || ''}
          onChange={(value) => handleFieldChange('nome_cliente', value)}
          label="Escreva o nome do seu cliente:"
        />

        {/* 2. Fatos do Caso (Obrigatório) */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Quais são os fatos do caso? <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={8}
            placeholder="Ex: O autor, Igor Pereira Severino, atualmente com 6 anos, é filho do Requerido Nathan Lucca Severino, fruto da relação com a genitora Bianca Aparecida Pereira..."
            value={values.fatos_caso || ''}
            onChange={(e) => handleFieldChange('fatos_caso', e.target.value)}
            inputProps={{ maxLength: 10000 }}
          />
          <Typography 
            variant="caption" 
            color={(values.fatos_caso || '').length >= 200 ? 'success.main' : 'text.secondary'} 
            sx={{ mt: 0.5, display: 'block' }}
          >
            {(values.fatos_caso || '').length} de 10000 caracteres permitidos
            {(values.fatos_caso || '').length < 200 && ` (mínimo: 200)`}
            {(values.fatos_caso || '').length >= 200 && ' ✓'}
          </Typography>
        </Box>

        {/* 3. Parte Contrária */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Escreva o nome da parte contrária: <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            placeholder="Ex: O réu é Manuel Pereira."
            value={values.parte_contraria || ''}
            onChange={(e) => handleFieldChange('parte_contraria', e.target.value)}
          />
        </Box>

        {/* 4. Upload de Documentos */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="Documentos que comprovem o fumus boni iuris e o periculum in mora."
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />

        {/* Gravação de Áudio */}
        <AudioRecordingSection
          onRecordingComplete={handleRecordingComplete}
          description="Grave um áudio descrevendo a urgência e os fatos do caso que a IA irá transcrever automaticamente."
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Estilo de Escrita */}
      <WritingStyleSelector
        selectedStyle={selectedStyle}
        onStyleChange={(styleId) => {
          setSelectedStyle(styleId);
          handleFieldChange('estilo_escrita', styleId);
        }}
      />

      <InfoTip message="Na tutela cautelar antecedente, demonstre claramente o perigo de dano (periculum in mora) e a probabilidade do direito (fumus boni iuris). Anexe documentos que comprovem a urgência da medida." />
    </Box>
  );
};

export default TutelaCautelarForm;

